import React, { useState } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import Button from '@atlaskit/button';
import { DropdownItem as MeatballsMenuItem } from '@atlaskit/dropdown-menu';
import FeedbackIcon from '@atlaskit/icon/glyph/feedback';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useInsightFeedbackCollector,
	OBJECT_VIEW_LOCATION,
} from '@atlassian/jira-servicedesk-insight-common-cmdb-feedback-collector/src/index.tsx';
import { QrCodeIcon } from '@atlassian/jira-servicedesk-insight-glyphs/src/index.tsx';
import { AsyncCloneObjectModal } from '@atlassian/jira-servicedesk-insight-object-clone-modal/src/async.tsx';
import { AsyncDeleteObjectModal } from '@atlassian/jira-servicedesk-insight-object-delete-modal/src/async.tsx';
import {
	useObjectStoreState,
	useObjectSchemaId,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { AsyncSingleQrCodeModal } from '@atlassian/jira-servicedesk-insight-object-qr-code-modal/src/async.tsx';
import { COLUMN_GUTTERS } from '@atlassian/jira-servicedesk-insight-object-view-layout/src/ui/index.tsx';
import {
	toSchemaId,
	toCmdbObjectTypeId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { BackLink } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/back-link/index.tsx';
import { MeatballsMenu } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/meatballs-menu/main.tsx';
import { createObjectSchemaUrl } from '@atlassian/jira-servicedesk-insight-urls/src/index.tsx';
import { InsightObjectViewHeaderSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/index.tsx';
import type { BackButtonProps } from '../../common/types.tsx';
import { Breadcrumbs } from './breadcrumbs/index.tsx';
import { IconEditDialog } from './icon-edit-dialog/index.tsx';
import messages from './messages.tsx';
import { ObjectNameInlineEdit } from './name-inline-edit/index.tsx';

export type Props = {
	isMultiColumn: boolean;
	canEdit: boolean;
	canDelete: boolean;
	backButton?: BackButtonProps;
	showFullBreadcrumbTrail?: boolean;
	redirectOnDelete: boolean;
	onClone: () => void;
	onDelete: () => void;
};

export const ObjectViewHeader = (props: Props) => {
	const {
		canEdit,
		canDelete,
		backButton,
		isMultiColumn,
		onDelete,
		onClone,
		redirectOnDelete,
		showFullBreadcrumbTrail = true,
	} = props;

	const { formatMessage } = useIntl();
	const [{ loading, objectDetails, hideEmptyAttributes }, { setHideEmptyAttributesState }] =
		useObjectStoreState();

	const [objectSchemaId] = useObjectSchemaId();

	const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isCloneModalOpen, setCloneModalOpen] = useState<boolean>(false);
	const [isQrCodeModalOpen, setQrCodeModalOpen] = useState<boolean>(false);

	const showFullTrail = backButton === undefined ? showFullBreadcrumbTrail : !backButton;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openFeedbackDialog } = useInsightFeedbackCollector(OBJECT_VIEW_LOCATION);

	if (loading || !objectDetails || !objectSchemaId) {
		return <InsightObjectViewHeaderSkeleton />;
	}

	const renderHideEmptyAttributesMeatballs = () => {
		if (hideEmptyAttributes === true) {
			return (
				<MeatballsMenuItem
					onClick={() => {
						setHideEmptyAttributesState(false);
					}}
				>
					{formatMessage(messages.showEmptyAttributes)}
				</MeatballsMenuItem>
			);
		}
		return (
			<MeatballsMenuItem
				onClick={() => {
					setHideEmptyAttributesState(true);
				}}
			>
				{formatMessage(messages.hideEmptyAttributes)}
			</MeatballsMenuItem>
		);
	};

	return (
		<>
			<PreHeaderContainer>
				{backButton && (
					<BackLink to={backButton.to} onClick={backButton.onClick}>
						{backButton.label}
					</BackLink>
				)}
				<Breadcrumbs showFullTrail={showFullTrail} />
			</PreHeaderContainer>
			<HeaderContainer isMultiColumn={isMultiColumn}>
				<ObjectNameContainer>
					<IconContainer>
						<IconEditDialog canEdit={canEdit} avatar={objectDetails.avatar} />
					</IconContainer>
					<InlineEditContainer>
						<ObjectNameInlineEdit canEdit={canEdit} />
					</InlineEditContainer>
				</ObjectNameContainer>
				<ActionsContainer>
					<li>
						<Button
							appearance="subtle"
							aria-label="header-feedback"
							iconBefore={<FeedbackIcon label={formatMessage(messages.giveFeedbackLabel)} />}
							onClick={() => openFeedbackDialog()}
							testId="servicedesk-insight-object-view.ui.header.feedback-button"
						/>
					</li>
					<li>
						<Button
							aria-label="qrcode-button"
							appearance="subtle"
							iconBefore={<QrCodeIcon label={formatMessage(messages.printQrCodeLabel)} />}
							onClick={() => setQrCodeModalOpen(true)}
							testId="servicedesk-insight-object-view.ui.header.qrcode-button"
						/>
					</li>
					{canEdit ? (
						<li>
							<MeatballsMenu
								moreIconLabel={formatMessage(messages.moreActions)}
								testId="servicedesk-insight-object-view.ui.header.meatballs-menu"
							>
								{renderHideEmptyAttributesMeatballs()}
								<MeatballsMenuItem
									data-testId="servicedesk-insight-object-view.ui.header.meatballs-menu-clone"
									data-test-id="servicedesk-insight-object-view.ui.header.meatballs-menu-clone"
									onClick={() => {
										fireUIAnalytics(
											createAnalyticsEvent({}),
											'button clicked',
											'openInsightObjectCloneModal',
										);
										setCloneModalOpen(true);
									}}
								>
									{formatMessage(messages.clone)}
								</MeatballsMenuItem>
								{canDelete && (
									<MeatballsMenuItem
										data-testId="servicedesk-insight-object-view.ui.header.meatballs-menu-delete"
										data-test-id="servicedesk-insight-object-view.ui.header.meatballs-menu-delete"
										onClick={() => {
											fireUIAnalytics(
												createAnalyticsEvent({}),
												'button clicked',
												'openInsightObjectDeleteModal',
											);
											setDeleteModalOpen(true);
										}}
									>
										{formatMessage(messages.delete)}
									</MeatballsMenuItem>
								)}
							</MeatballsMenu>
						</li>
					) : null}
				</ActionsContainer>
			</HeaderContainer>

			<AsyncDeleteObjectModal
				objectId={objectDetails.id}
				objectLabel={objectDetails.label}
				onSuccessRedirectUrl={
					redirectOnDelete ? createObjectSchemaUrl(toSchemaId(objectSchemaId)) : undefined
				}
				onSuccess={onDelete}
				onClose={() => setDeleteModalOpen(false)}
				isDeleteModalOpen={isDeleteModalOpen}
			/>
			<AsyncCloneObjectModal
				objectId={objectDetails.id}
				objectDetails={objectDetails}
				objectSchemaId={toSchemaId(objectSchemaId)}
				objectTypeId={toCmdbObjectTypeId(objectDetails.objectType.id)}
				onSuccess={onClone}
				onClose={() => setCloneModalOpen(false)}
				isCloneModalOpen={isCloneModalOpen}
			/>

			<AsyncSingleQrCodeModal
				objectId={objectDetails.id}
				objectSchemaId={toSchemaId(objectSchemaId)}
				objectTypeId={toCmdbObjectTypeId(objectDetails.objectType.id)}
				onClose={() => setQrCodeModalOpen(false)}
				isQrCodeModalOpen={isQrCodeModalOpen}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectNameContainer = styled2.div({
	boxSizing: 'border-box',
	display: 'flex',
	width: '100%',
	wordBreak: 'break-all',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.ul({
	display: 'flex',
	justifyContent: 'flex-end',
	boxSizing: 'border-box',
	gap: token('space.200', '16px'),
	flexShrink: 0,
	paddingLeft: 0,
	listStyleType: 'none',
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> li': {
		marginTop: 0,
	},
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled.div<{ isMultiColumn: boolean }>`
	display: flex;
	align-items: center;
	gap: ${token('space.200', '16px')};
	margin: ${token('space.200', '16px')} 0;
	padding-top: ${token('space.100', '8px')};
	padding-left: ${token('space.100', '8px')};
	flex-wrap: wrap;

	${({ isMultiColumn }) =>
		isMultiColumn &&
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		css`
			flex-wrap: nowrap;
			padding-top: 0;
			gap: ${COLUMN_GUTTERS}px;

			${ActionsContainer}: {
				margin-bottom: ${token('space.200', '16px')};
			}
		`};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineEditContainer = styled.div({
	flex: 1,
	/* override AK default margin applied to inline edit */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'form > div': {
		marginTop: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreHeaderContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200', '16px'),
	paddingLeft: token('space.100', '8px'),
	/* Prevents layout shifts when switching between Breadcrumsb/BackButton view states */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 4}px`,
});
